/**
 * Log actions are used to track user interactions and system events.
 * Please follow the syntax while creating new category of enums i.e. <category>LogAction
 * e.g. for Account -> AccountLogAction
 */
export type TrackedAction =
  | ElementLogAction
  | AppLogAction
  | TabLogAction
  | PackageLogAction
  | AnalysisLogAction
  | ProposalLogAction
  | ErrorLogAction
  | ViewLogAction
  | AccountLogAction
  | ReportLogAction
  | MetricLogAction
  | ModelLogAction
  | FilterLogAction
  | BenchmarkLogAction
  | ModalLogAction
  | UserOptionLogAction
  | TableLogAction
  | ApiRetryLogAction
  | ProposalGuideApiAction
  | ColumnSelectorLogAction
  | UapDocumentLogAction
  | AGGridLogAction
  | BackToAction
  | UapLogAction
  | WalkMeLogAction
  | ProposalGuideAction
  | ActionsWorkflowAction
  | ChartLogAction
  | PortfolioAction
  | AdvisoryFeeAction
  | CommonLogActions
  | ExposureSearchAction
  | PkceLogAction
  | FDC3Action
  | BatchProposalGuideAction
  | OrderReviewLogAction
  | ModelEvaluatorAction
  | DashBoardLogAction
  | ProspectLogAction
  | ExhibitInteractiveAction
  | PartnerIntegrationAction
  | BulkSecurityAnalyzerLogAction
  | ScenarioTesterLogAction;

enum ElementLogAction {
  ELEMENT_CLICKED = 'element-clicked',
  HYPERLINK_CLICKED = 'hyperlink-clicked',
  REBALANCE_START = 'rebalance-start',
}

enum AppLogAction {
  APP_CHANGE = 'app-change',
  APP_INITIALIZE_TELEMETRY = 'app-initialize-telemetry',
  APP_LOADED = 'app-loaded',
  APP_RELOAD = 'app-reload',
  APP_TIMEOUT_RELOAD = 'app-timeout-reload',
  BROWSER_INFO = 'browser-info',
  CLEARED_SP_ARRAY = 'cleared-sp-array',
  DEBUG_LOCALIZATIONS = 'debug-localizations',
  NEW_DATES_AVAILABLE = 'new-dates-available',
  NEW_VERSION_AVAILABLE = 'new-version-available',
  SESSION_EXPIRY_REDIRECT_DETECTED = 'session-expiry-redirect-detected',
  TOOL_COMPLETE = 'tool-complete',
}

enum TabLogAction {
  TAB_ADDED = 'tab-added',
  TAB_CHANGE = 'tab-change',
  TAB_DELETED = 'tab-deleted',
}

enum ExposureSearchAction {
  EXPOSURE_SEARCH_ERROR = 'exposure-search-error',
  NEW_EXPOSURE_SEARCH = 'new-exposure-search',
  SUBMIT_EXPOSURE_SEARCH = 'submit-exposure-search',
}
enum PackageLogAction {
  PKG_CHANGE = 'package-change',
  PKG_REDIRECT = 'package-redirect',
}

enum AnalysisLogAction {
  ANALYSIS_DATE_CHANGE = 'analysis-date-change',
}

enum ProposalLogAction {
  CREATE_PROPOSAL = 'create-proposal',
  CREATE_PROPOSAL_SETUP = 'create-proposal-setup',
  DELETE_PROPOSAL = 'delete-proposal',
  LAUNCH_PROPOSAL = 'launch-proposal',
  LAUNCH_PROPOSAL_CHECKOUT_DEEPLINK = 'launch-proposal-checkout-deeplink',
  OPTIMIZE_PROPOSAL = 'optimize-proposal',
  PROPOSAL_CREATION_FAILED = 'proposal-creation-failed',
  PROPOSAL_NAME_CHANGE = 'proposal-name-change',
  PROPOSAL_STATUS_CHANGE = 'proposal-status-change',
  START_PROPOSAL_CREATION = 'start-proposal-creation',
  START_PROPOSAL_GUIDE = 'start-proposal-guide',
}

enum ProspectLogAction {
  LAUNCH_PROSPECT = 'launch-prospect',
}

enum BulkSecurityAnalyzerLogAction {
  DOWNLOAD_ANALYSIS = 'download-analysis',
}

enum ErrorLogAction {
  FARO_INTEGRATION_FAILURE = 'faro-integration-failure',
  FARO_INTEGRATION_SUCCESS = 'faro-integration-success',
  FATAL_FRONTEND_ERROR = 'fatal-frontend-error',
  FRONTEND_ERROR = 'frontend-error',
  HTTP_ERROR = 'http-error',
  RECOVERED_ERROR = 'recovered-error',
  SENSITIVE_DATA_COPY_PASTED = 'sensitive-data-copy-pasted',
  SENSITIVE_DATA_DETECTED = 'sensitive-data-detected',
}

enum ApiRetryLogAction {
  API_CALL_FAILED_AFTER_RETRY = 'api-call-failed-after-retry',
  API_CALL_SUCCEEDED_AFTER_RETRY = 'api-call-succeeded-after-retry',
  HTTP_REQUEST = 'http-request',
}

enum ViewLogAction {
  LAUNCH_ACTIONS = 'launch-actions',
  LAUNCH_CLIENTS = 'launch-clients',
  VIEW_AS_CHANGED = 'view-as-changed',
  VIEW_CHANGED = 'view-changed',
  VIEW_SELECTOR_CHANGED = 'view-selector-changed',
}
enum ScenarioTesterLogAction {
  CREATE_SCENARIO_GROUP = 'create-scenario-group',
  RESET_SCENARIOS = 'reset-scenarios',
  SCENARIO_CARD_COLLAPSED = 'scenario-card-collapsed',
  SCENARIO_CARD_EXPANDED = 'scenario-card-expanded',
  SCENARIO_CHANGED = 'scenario-changed',
  SCENARIO_GROUP_SAVED = 'scenario-group-saved',
  SCENARIOS_VIEW_BY = 'scenarios-view-by-changed',
  SHOW_HIDE_SCENARIOS = 'show-hide-scenarios',
}
enum AccountLogAction {
  ACCOUNT_CLICKED = 'account-clicked',
  ADD_ACCOUNT = 'add-account',
  AGGREGATE_CLICKED = 'aggregate-clicked',
  LAUNCH_DEEP_DIVE = 'launch-deep-dive',
  SLEEVE_CLICKED = 'sleeve-clicked',
  SUB_SLEEVE_CLICKED = 'sub-sleeve-clicked',
}

enum ReportLogAction {
  REPORT_AI_COMMENTARY_ACKNOWLEDGED = 'report-ai-commentary-acknowledged',
  REPORT_ARCHIVED = 'report-archived',
  REPORT_AUTO_ATTACHMENT_ADDED = 'report-auto-attachment-added',
  REPORT_DOWNLOADED = 'report-downloaded',
  REPORT_LOCAL_DOWNLOAD = 'report-local-download',
  REPORT_MANUAL_ATTACHMENT_ADDED = 'report-manual-attachment-added',
  REPORT_PAGE_GROUP_SELECTION_TOGGLE = 'report-page-group-selection-toggle',
  REPORT_PREVIEW_CLOSED = 'report-preview-closed',
  REPORT_PREVIEWED = 'report-previewed',
  REPORT_TEMPLATE_CHANGED = 'report-template-changed',
  REPORT_USER_TEMPLATE_DELETED = 'report-user-template-deleted',
  REPORT_USER_TEMPLATE_DOWNLOADED = 'report-user-template-downloaded',
  REPORT_USER_TEMPLATE_SAVED = 'report-user-template-saved',
  REPORT_USER_TEMPLATE_SAVED_AND_DOWNLOADED = 'report-user-template-saved-and-downloaded',
  REPORT_USER_TEMPLATE_TOGGLE_FAVORITE = 'report-user-template-toggle-favorite',
}

enum UapDocumentLogAction {
  DOCUMENT_DOWNLOADED = 'document-downloaded',
}

enum UapLogAction {
  ATTACHMENT_CLICK = 'attachment-click',
  ENROLLMENT_SUBMITTED = 'enrollment-submitted',
  SERVICE_REQUEST_SUBMITTED = 'service-request-submitted',
  SERVICING_DOCUMENT_UPLOAD_FAILED = 'servicing-document-upload-failed',
}

enum MetricLogAction {
  DIFFERENCE_MARKET_VALUE_UPDATED = 'difference-market-value-updated',
  DIFFERENCE_NOTIONAL_MARKET_VALUE_UPDATED = 'difference-notional-market-value-updated',
  DIFFERENCE_NOTIONAL_WEIGHT_UPDATED = 'difference-notional-weight-updated',
  DIFFERENCE_QUANTITY_UPDATED = 'difference-quantity-updated',
  DIFFERENCE_WEIGHT_UPDATED = 'difference-weight-updated',
  INFLATION_UPDATED = 'inflation-updated',
  MARKET_VALUE_UPDATED = 'market-value-updated',
  NET_ALPHA_UPDATED = 'net-alpha-updated',
  NOTIONAL_MARKET_VALUE_UPDATED = 'notional-market-value-updated',
  NOTIONAL_WEIGHT_UPDATED = 'notional-weight-updated',
  PERIOD_UPDATED = 'period-updated',
  PROPOSE_CHANGE = 'propose-change',
  QUANTITY_UPDATED = 'quantity-updated',
  WEIGHT_UPDATED = 'weight-updated',
}

enum FilterLogAction {
  CLEAR_ALL_FILTERS = 'clear-all-filters',
  CLEAR_FILTER = 'clear-filter',
  CLEAR_FILTERS = 'clear-filters',
  FILTER_BUTTON_CLICKED = 'filter-button-clicked',
  FILTER_CLOSED = 'filter-closed',
  FILTER_TOGGLE = 'filter-toggle',
  GENERIC_FACETED_FILTER_APPLIED = 'generic-faceted-filter-applied',
  GENERIC_FACETED_FILTER_CLOSED = 'generic-faceted-filter-closed',
  GENERIC_FACETED_FILTER_OPEN = 'generic-faceted-filter-open',
  GENERIC_FACETED_FILTER_RESET = 'generic-faceted-filter-reset',
  QUICK_FILTER_APPLIED = 'quick-filter-applied',
  QUICK_FILTER_REMOVED = 'quick-filter-removed',
}

enum BenchmarkLogAction {
  APPLY_BENCHMARK_CHANGE = 'apply-benchmark-change',
  CANCEL_BENCHMARK_CHANGE = 'cancel-benchmark-change',
  EDIT_BENCHMARK = 'edit-benchmark',
  OPEN_CUSTOM_BENCHMARK_MODAL = 'open-custom-benchmark-modal',
  REVERT_TO_ASSIGNED_BENCHMARK = 'revert-to-assigned-benchmark',
  SET_TO_NONE_BENCHMARK = 'set-to-none-benchmark',
}

enum ModalLogAction {
  CANCEL_MODAL = 'cancel-modal',
  OPEN_CUSTOM_MODEL_MODAL = 'open-custom-model-modal',
  OPEN_UPDATE_STATUS_MODAL = 'open-update-status-modal',
  STEPPER_ACTION_BUTTON_CLICKED = 'stepper-action-button-clicked',
}

enum ModelLogAction {
  APPLY_MODEL_CHANGE = 'apply-model-change',
  CANCEL_MODEL_CHANGE = 'cancel-model-change',
  CLEAR_MODEL_SEARCH = 'clear-model-search',
  CREATE_MODEL = 'create-model',
  CREATE_NEW_MODEL = 'create-new-model',
  MODEL_CREATED = 'model-created',
  MODEL_SEARCH = 'model-search',
  MODELS_BULK_EDIT = 'models-bulk-edit',
  MODELS_BULK_EDIT_REALLOCATE_ACTION = 'models-bulk-edit-reallocate-action',
  MODELS_BULK_EDIT_REMOVE_FROM_GROUP = 'models-bulk-edit-remove-from-group',
  MODELS_BULK_EDIT_TABLE_EDIT = 'models-bulk-edit-table-edit',
  MODELS_BULK_EDIT_VALIDATION_STATUS = 'models-bulk-edit-validation-status',
  MODELS_CREATE_ALLOCATION = 'models-create-allocation',
  MODELS_UPDATE_ALLOCATION = 'models-update-allocation',
  REVERT_TO_ASSIGNED_MODEL = 'revert-to-assigned-model',
  SET_TO_NONE_MODEL = 'set-to-none-model',
  VIEW_MODEL_FAMILY_DETAILS = 'view-model-family-details',
}

enum UserOptionLogAction {
  AGG_TAG_CHANGED = 'agg-tag-changed',
  EXPORT_TO_EXCEL = 'export-to-excel',
  GROUP_BY_UPDATED = 'group-by-updated',
  SETTINGS_OPTION_CLICKED = 'settings-option-clicked',
  USER_OPTION_CLICKED = 'user-option-clicked',
}

enum TableLogAction {
  ENTITY_SEARCH_SUGGESTION_SELECTION = 'entity-search-suggestion-selection',
  ROW_GROUP_TOGGLE = 'row-group-toggle',
}

enum ColumnSelectorLogAction {
  APPLY_COLUMN_SELECTOR = 'apply-column-selector',
  CHANGE_COLUMN_ORDER = 'change-column-order',
}

enum WalkMeLogAction {
  WALKME_FLOW_ABORTED = 'walkme-flow-aborted',
  WALKME_FLOW_COMPLETED_STOPPED = 'walkme-flow-completed/stopped',
  WALKME_FLOW_STARTED = 'walkme-flow-started',
  WALKME_LAUNCHER_CLICKED = 'walkme-launcher-clicked',
  WALKME_NEXT_STEP_CLICKED = 'walkme-next-step-clicked',
  WALKME_PREV_STEP_CLICKED = 'walkme-prev-step-clicked',
}
enum AGGridLogAction {
  AG_GRID_ACTION_CELL_CLICKED = 'action-cell-clicked',
  AG_GRID_BODY_SCROLL = 'ag-grid-body-scroll',
  AG_GRID_BODY_SCROLL_END = 'ag-grid-body-scroll-end',
  AG_GRID_CELL_CLICKED = 'ag-grid-cell-clicked',
  AG_GRID_CELL_ENTER_KEY_DOWN = 'ag-grid-cell-enter-key-down',
  AG_GRID_CELL_VALUE_CHANGED = 'ag-grid-cell-value-changed',
  AG_GRID_HEADER_CLICK = 'ag-grid-header-click',
  AG_GRID_ROW_CLICKED = 'ag-grid-row-clicked',
  AG_GRID_ROW_GROUP_COLLAPSED = 'ag-grid-row-group-collapsed',
  AG_GRID_ROW_GROUP_EXPANDED = 'ag-grid-row-group-expanded',
  AG_GRID_SORT_CHANGED = 'ag-grid-sort-changed',
}

enum BackToAction {
  BACK_TO_HOME = 'back-to-feature',
}

enum ProposalGuideApiAction {
  PROPOSAL_GUIDE_ANALYZE_FAILED = 'proposal-guide-analyze-failed',
  PROPOSAL_GUIDE_ANALYZE_SUCCEEDED = 'proposal-guide-analyze-succeeded',
  PROPOSAL_GUIDE_ANALYZE_SUCCEEDED_NO_TRADES = 'proposal-guide-analyze-succeeded-no-trades',
  PROPOSAL_GUIDE_GENERATE_FAILED = 'proposal-guide-generate-failed',
  PROPOSAL_GUIDE_GENERATE_SUCCEEDED = 'proposal-guide-generate-succeeded',
}

enum ProposalGuideAction {
  PROPOSAL_GUIDE_BACK = 'proposal-guide-back',
  PROPOSAL_GUIDE_CANCEL = 'proposal-guide-cancel',
  PROPOSAL_GUIDE_EXIT_BACK = 'proposal-guide-exit-back',
  PROPOSAL_GUIDE_EXIT_CANCEL = 'proposal-guide-exit-cancel',
  PROPOSAL_GUIDE_NEXT = 'proposal-guide-next',
  PROPOSAL_GUIDE_NEXT_CAMPAIGN_SELECTED = 'proposal-guide-next-campaign-selected',
  PROPOSAL_GUIDE_NEXT_NO_CAMPAIGN_SELECTED = 'proposal-guide-next-no-campaign-selected',
  PROPOSAL_GUIDE_OPTIMIZE = 'proposal-guide-optimize',
  PROPOSAL_GUIDE_REVIEW_APPLY = 'proposal-guide-review-apply',
  PROPOSAL_GUIDE_REVIEW_BACK = 'proposal-guide-review-back',
  PROPOSAL_GUIDE_REVIEW_CANCEL = 'proposal-guide-review-cancel',
  PROPOSAL_GUIDE_START = 'proposal-guide-start',
}

enum ActionsWorkflowAction {
  ACTIONS_WORKFLOW_STATUS_UPDATED = 'actions-workflow-status-updated',
}

enum OrderReviewLogAction {
  ORDER_BULK_STATUS_TRANSITION_FAILED = 'order-bulk-status-transition-failed',
  ORDER_BULK_STATUS_TRANSITION_SUCCEEDED = 'order-bulk-status-transition-succeeded',
  ORDER_PROPOSAL_ACTIONS = 'order-proposal-actions',
  ORDER_STATUS_TRANSITION_FAILED = 'order-status-transition-failed',
  ORDER_STATUS_TRANSITION_SUCCEEDED = 'order-status-transition-succeeded',
  START_RAISE_ORDER = 'start-raise-order',
}

// add chart specific action
enum ChartLogAction {
  CHART_CLICKED = 'chart-clicked',
}
enum CommonLogActions {
  TABLE_SEARCH = 'table-search',
}

enum PortfolioAction {
  ADD_AGGREGATE_NEW = 'add-aggregate-new',
  ADD_AGGREGATE_SAVED = 'add-aggregate-saved',
  ADD_BOOK_SYNC_CLIENT_ACCOUNT = 'add-book-sync-client-account',
  ADD_HOLDINGS_FROM_FILE = 'add-holdings-from-file',
  ADD_HOLDINGS_MANUALLY = 'add-holdings-manually',
  ADD_MMA_CLIENT_ACCOUNT = 'add-mma-client-account',
  ADD_PORTFOLIO_MODEL = 'add-portfolio-model',
  ADD_PORTFOLIO_NEW = 'add-portfolio-new',
  ADD_PORTFOLIO_PARTNER = 'add-portfolio-partner',
  ADD_PORTFOLIO_SAVED = 'add-portfolio-saved',
  ADD_PORTFOLIO_TO_COMPARE = 'add-to-compare',
  ADD_PRODUCT_CASH = 'add-product-cash',
  ADD_PRODUCT_COPY_FROM_MODEL = 'add-product-copy-from-model',
  ADD_PRODUCT_SECURITY_FINDER = 'add-product-security-finder',
  ADD_PRODUCT_UPLOAD_FROM_FILE = 'add-product-upload-from-file',
  ADD_SECURITIES_SECURITY_FINDER = 'add-securities-security-finder',
  ADD_SECURITIES_SECURITY_SEARCH = 'add-securities-security-search',
  ANALYZE_MODEL = 'analyze-model',
  ANALYZE_PORTFOLIO = 'analyze-portfolio',
  COMPARE_MODELS = 'compare-models',
  COPY_PORTFOLIO = 'copy-portfolio',
  CREATE_NEW_PROSPECT = 'create-new-prospect',
  CREATE_PORTFOLIO = 'create-portfolio',
  DOWNLOAD_TEMPLATE = 'download-template',
  EDIT_PORTFOLIO = 'edit-portfolio',
  IMPORTED_FROM_FILE = 'imported-from-file',
  MODEL_PORTFOLIO = 'model-portfolio',
  NEW_ANALYSIS = 'new-analysis',
  PORTFOLIO_DELETED = 'portfolio-deleted',
  PORTFOLIO_SELECTION_HEADER_CANCEL = 'portfolio-selection-header-cancel',
  QUICK_ACTIONS_EXPORT_EXCEL = 'quick-actions-export-excel',
  QUICK_ACTIONS_PRORATA = 'quick-actions-prorata',
  QUICK_ACTIONS_REMOVE_ALL = 'quick-actions-remove-all',
  QUICK_ACTIONS_ZERO_OUT = 'quick-actions-zero-out',
  REMOVE_PORTFOLIO_FROM_COMPARE = 'remove-portfolio-from-compare',
  SAVE_PORTFOLIO = 'save-portfolio',
  SAVED_PORTFOLIO = 'add-saved-portfolio',
  SELECTED_PORTFOLIO = 'selected-portfolio',
  SHARE_PORTFOLIO = 'share-portfolio',
}
enum AdvisoryFeeAction {
  ADVISORY_FEE_TOGGLE = 'advisory-fee-toggle',
  EDIT_ADVISORY_FEE = 'edit-advisory-fee',
  SET_ADVISORY_FEE = 'set-advisory-fee',
}

// PKCE authentication workflow actions
enum PkceLogAction {
  PKCE_ERROR = 'pkce-error',
  PKCE_WARNING = 'pkce-warning',
  PKCE_WORKFLOW = 'pkce-workflow',
}

enum FDC3Action {
  FDC3_DEEP_DIVE_INTENT_FAILED = 'fdc3-deep-dive-intent-failed',
  FDC3_DEEP_DIVE_INTENT_LAUNCHED = 'fdc3-deep-dive-intent-launched',
  FDC3_NAVIGATE_DEEP_DIVE = 'fdc3-navigate-deep-dive',
  FDC3_PDF_INTENT_LAUNCHED = 'fdc3-pdf-intent-launched',
  FDC3_RECEIVE_DEEP_DIVE_CONTEXT = 'fdc3-receive-deep-dive-context',
  FDC3_SET_DEEP_DIVE_CONTEXT = 'fdc3-set-deep-dive-context',
}

enum ModelEvaluatorAction {
  MODEL_CHECKBOX_DESELECTED = 'model-checkbox-deselected',
  MODEL_CHECKBOX_SELECTED = 'model-checkbox-selected',
  MODEL_FAMILY_DATA_FETCHED = 'model-family-data-fetched',
  MODEL_FAMILY_EXPANDED = 'model-family-expanded',
}

enum DashBoardLogAction {
  DASHBOARD_QUICK_ACTION_CLICK = 'dashboard-quick-action-click',
  SELECTION_CARD_ACTION_CLICK = 'selection-card-action-click',
}

enum BatchProposalGuideAction {
  REBALANCE_APPLY = 'rebalance-apply',
  REBALANCE_BACK = 'rebalance-back',
  REBALANCE_CANCEL = 'rebalance-cancel',
  REBALANCE_ERROR = 'rebalance-error',
  REBALANCE_EXIT_BACK = 'rebalance-exit-back',
  REBALANCE_EXIT_CANCEL = 'rebalance-exit-cancel',
  REBALANCE_NEXT = 'rebalance-next',
  REBALANCE_REVIEW_BACK = 'rebalance-review-back',
  REBALANCE_REVIEW_CANCEL = 'rebalance-review-cancel',
  REBALANCE_START = 'rebalance-start',
}

enum ExhibitInteractiveAction {
  CONTENT_ON_CLICK = 'content-on-click',
  ON_ROW_CONTENT_CLICK = 'on-row-content-click',
  SIDE_PANEL_CLOSED = 'side-panel-closed',
}

enum PartnerIntegrationAction {
  BOOK_SYNC_OFF = 'book-sync-off',
  PARTNER_MODAL_FINISH_STEP = 'partner-modal-finish-step',
  PARTNER_MODAL_NEXT_STEP = 'partner-modal-next-step',
  PARTNER_MODAL_PREV_STEP = 'partner-modal-prev-step',
  PARTNER_PLATFORM_AUTH_ERROR = 'partner-platform-auth-error',
  PARTNER_PLATFORM_AUTH_POPUP_CLOSED = 'partner-platform-auth-popup-closed',
  PARTNER_PLATFORM_AUTH_POPUP_OPENED = 'partner-platform-auth-popup-opened',
  PARTNER_PLATFORM_AUTH_SUCCESS = 'partner-platform-auth-success',
  PARTNER_PLATFORM_SELECTED = 'partner-platform-selected',
  PORTFOLIO_ANALYZER_CANCELED = 'portfolio-analyzer-canceled',
  PORTFOLIO_ANALYZER_LAUNCHED = 'portfolio-analyzer-launched',
  SECURITIES_REPLACED = 'securities-replaced',
  SECURITY_REPLACEMENT_DELETED = 'security-replacement-deleted',
}

export const LogAction = {
  ...ElementLogAction,
  ...AppLogAction,
  ...TabLogAction,
  ...PackageLogAction,
  ...AnalysisLogAction,
  ...ProposalLogAction,
  ...ErrorLogAction,
  ...ViewLogAction,
  ...AccountLogAction,
  ...ReportLogAction,
  ...MetricLogAction,
  ...FilterLogAction,
  ...BenchmarkLogAction,
  ...ModalLogAction,
  ...ModelLogAction,
  ...ProposalGuideApiAction,
  ...UserOptionLogAction,
  ...TableLogAction,
  ...ColumnSelectorLogAction,
  ...WalkMeLogAction,
  ...BackToAction,
  ...ApiRetryLogAction,
  ...UapDocumentLogAction,
  ...AGGridLogAction,
  ...UapLogAction,
  ...ProposalGuideAction,
  ...ActionsWorkflowAction,
  ...ChartLogAction,
  ...PortfolioAction,
  ...AdvisoryFeeAction,
  ...CommonLogActions,
  ...ExposureSearchAction,
  ...PkceLogAction,
  ...FDC3Action,
  ...BatchProposalGuideAction,
  ...OrderReviewLogAction,
  ...ModelEvaluatorAction,
  ...DashBoardLogAction,
  ...ProspectLogAction,
  ...ExhibitInteractiveAction,
  ...PartnerIntegrationAction,
  ...BulkSecurityAnalyzerLogAction,
  ...ScenarioTesterLogAction,
};
